import { create } from 'zustand';
import type { DiagnosticState, DiagnosticMode, DiagnosticMessage } from '../types/diagnostic';
import type { CogniPrintProfile, AnchorItemResponse, DiagnosticDimensionKey } from '../types/cogniprint';
import { anchorItemsForMode } from '../diagnostic/anchor-items';

const initialState: DiagnosticState = {
  mode: 'quick',
  status: 'not_started',
  messages: [],
  currentQuestionIndex: 0,
  totalEstimatedQuestions: 8,
  dimensionsCompleted: 0,
  totalDimensions: 4,
  startedAt: null,
  completedAt: null,
  profile: null,
  error: null,
  itemResponses: [],
  currentItemId: null,
  followedUp: [],
  activeItemId: null,
  disputed: [],
  pendingDispute: null,
  sessionId: null,
};

interface DiagnosticStore extends DiagnosticState {
  startDiagnostic: (mode: DiagnosticMode) => void;
  addMessage: (message: DiagnosticMessage) => void;
  updateProgress: (questionIndex: number, dimensionsCompleted: number) => void;
  setProfile: (profile: CogniPrintProfile) => void;
  setStatus: (status: DiagnosticState['status']) => void;
  setError: (error: string | null) => void;
  /** Replace the session's recorded answers. The app is the only writer. */
  setProgress: (progress: {
    itemResponses: AnchorItemResponse[];
    followedUp: string[];
    activeItemId?: string | null;
    disputed?: DiagnosticDimensionKey[];
  }) => void;
  setCurrentItem: (itemId: string | null) => void;
  requestDispute: (dimension: DiagnosticDimensionKey, words: string) => void;
  clearPendingDispute: () => void;
  setSessionId: (sessionId: string) => void;
  reset: () => void;
}

export const useDiagnosticStore = create<DiagnosticStore>((set) => ({
  ...initialState,

  startDiagnostic: (mode) =>
    set({
      ...initialState,
      mode,
      status: 'in_progress',
      totalDimensions: mode === 'quick' ? 4 : 7,
      // Derived from the anchored item bank rather than hard-coded, so the progress
      // bar cannot drift away from the instrument. A clarifying follow-up can push
      // the real count a little past this; the bar clamps.
      totalEstimatedQuestions: anchorItemsForMode(mode).length,
      startedAt: new Date().toISOString(),
    }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateProgress: (questionIndex, dimensionsCompleted) =>
    set({ currentQuestionIndex: questionIndex, dimensionsCompleted }),

  setProfile: (profile) =>
    set({
      profile,
      status: 'complete',
      completedAt: new Date().toISOString(),
    }),

  setStatus: (status) => set({ status }),

  setError: (error) => set({ error, status: error ? 'not_started' : 'in_progress' }),

  setProgress: ({ itemResponses, followedUp, activeItemId, disputed }) =>
    set((state) => ({
      itemResponses,
      followedUp,
      activeItemId: activeItemId === undefined ? state.activeItemId : activeItemId,
      disputed: disputed ?? state.disputed,
    })),

  setCurrentItem: (currentItemId) => set({ currentItemId }),

  requestDispute: (dimension, words) => set({ pendingDispute: { dimension, words } }),

  clearPendingDispute: () => set({ pendingDispute: null }),

  setSessionId: (sessionId) => set({ sessionId }),

  reset: () => set(initialState),
}));
